import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { API } from '../utils/api';
import { decodeResult, redactedReturnParams, suspiciousReason } from '../utils/swishReturn';

const POLL_MS = 3000;
const POLL_MAX = 10;
const FAILED = ['CANCELLED', 'DECLINED', 'ERROR', 'EXPIRED'];
const REQ = { timeout: 10000 };

export function stateFor(status) {
  if (status === 'COMPLETED' || status === 'PAID') return 'success';
  if (status === 'SUSPICIOUS') return 'suspicious';
  if (FAILED.includes(status)) return 'failed';
  return 'pending';
}

// V6.2.3 return-screen flow. `endpoints` lets the guest page reuse it with its own URLs.
export function useSwishReturn(info, endpoints) {
  const [state, setState] = useState('loading');
  const [details, setDetails] = useState(null);
  const [reason, setReason] = useState(null);
  const [ticketReported, setTicketReported] = useState(false);
  const events = useRef([]);
  const reported = useRef(false);
  const started = useRef(false);
  const { paymentId, nonce, result } = info;

  const log = useCallback((e, d) => { events.current.push({ t: new Date().toISOString(), e, ...(d || {}) }); }, []);

  const apply = useCallback((data, layers) => {
    setDetails(data);
    const next = stateFor(data?.status);
    if (next === 'suspicious') setReason(suspiciousReason(layers?.length ? layers : data?.mismatch_layers));
    setState(next);
    return next;
  }, []);

  const fetchStatus = useCallback(async () => {
    const res = await axios.get(endpoints.status(paymentId, nonce), REQ);
    log('status', { status: res.data?.status });
    return res.data;
  }, [endpoints, paymentId, nonce, log]);

  const report = useCallback(async () => {
    if (reported.current || !paymentId || !nonce) return;
    reported.current = true;
    try {
      await axios.post(`${API}/payment/${paymentId}/report-unclear`, {
        nonce, events: events.current, return_params: redactedReturnParams(info),
      }, REQ);
      setTicketReported(true);
    } catch { /* support still sees the payment in the admin queue */ }
  }, [paymentId, nonce, info]);

  useEffect(() => {
    if (started.current || !paymentId || !nonce) return;
    started.current = true;
    (async () => {
      log('return_opened', { has_result: !!result });
      let layers = null;
      let expired = false;
      if (result) {
        try {
          const res = await axios.post(endpoints.callback(paymentId), { nonce, result: decodeResult(result) }, REQ);
          log('callback', { status: res.data?.payment_status });
        } catch (e) {
          const code = e.response?.status;
          log('callback_error', { http: code });
          if (code === 404) return setState('notFound');
          if (code === 409) layers = e.response?.data?.detail?.mismatches || ['other'];
          if (code === 400) expired = true;
        }
      }
      try {
        const data = await fetchStatus();
        if (expired && stateFor(data?.status) === 'pending') { setDetails(data); return setState('expired'); }
        apply(layers ? { ...data, status: 'SUSPICIOUS' } : data, layers);
      } catch (e) {
        log('status_error', { http: e.response?.status });
        setState(e.response?.status === 404 ? 'notFound' : layers ? 'suspicious' : 'pending');
        if (layers) setReason(suspiciousReason(layers));
      }
    })();
  }, [paymentId, nonce, result, endpoints, fetchStatus, apply, log]);

  useEffect(() => {
    if (state !== 'pending' || !paymentId || !nonce) return undefined;
    let tries = 0;
    const timer = setInterval(async () => {
      tries += 1;
      try {
        const data = await fetchStatus();
        if (stateFor(data?.status) !== 'pending') { clearInterval(timer); apply(data); return; }
      } catch { /* keep polling */ }
      if (tries >= POLL_MAX) { clearInterval(timer); log('poll_timeout'); report(); }
    }, POLL_MS);
    return () => clearInterval(timer);
  }, [state, paymentId, nonce, fetchStatus, apply, log, report]);

  useEffect(() => {
    if (state === 'suspicious') report();
  }, [state, report]);

  return { state, setState, details, setDetails, reason, ticketReported };
}
